// Firebase Phone Auth setup.
//
// Architecture: the backend does NOT send SMS. Firebase (this client SDK) sends
// the SMS; the backend only verifies the resulting Firebase ID token and issues
// our JWT. The Firebase project MUST be `enturn-1879e` to match the backend —
// any other project makes token verification (and therefore login) fail.

import { getApp, getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type ConfirmationResult,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const firebaseAuth = getAuth(app);

export const RECAPTCHA_CONTAINER_ID = "recaptcha-container";

let recaptchaVerifier: RecaptchaVerifier | null = null;

function getRecaptchaVerifier(): RecaptchaVerifier {
  if (!recaptchaVerifier) {
    recaptchaVerifier = new RecaptchaVerifier(
      firebaseAuth,
      RECAPTCHA_CONTAINER_ID,
      { size: "invisible" }
    );
  }
  return recaptchaVerifier;
}

/** Clear the reCAPTCHA so a fresh one is created on the next attempt. */
export function resetRecaptcha() {
  if (recaptchaVerifier) {
    recaptchaVerifier.clear();
    recaptchaVerifier = null;
  }
}

/**
 * Ask Firebase to send the SMS OTP.
 * @param phone plain 10-digit number (no country code) — we prepend "+91".
 * @returns the ConfirmationResult used later to confirm the typed code.
 */
export async function sendFirebaseSmsOtp(
  phone: string
): Promise<ConfirmationResult> {
  try {
    return await signInWithPhoneNumber(
      firebaseAuth,
      `+91${phone}`,
      getRecaptchaVerifier()
    );
  } catch (err) {
    // A failed attempt can leave the reCAPTCHA in a stale state; reset it so a
    // retry can mint a fresh token.
    resetRecaptcha();
    throw err;
  }
}

/**
 * Confirm the 6-digit code the user typed and return the Firebase ID token.
 * This ID token (NOT the 6-digit code) is what the backend expects in `otp`.
 */
export async function confirmFirebaseSmsOtp(
  confirmation: ConfirmationResult,
  code: string
): Promise<string> {
  const credential = await confirmation.confirm(code);
  return credential.user.getIdToken();
}
